/* eslint-disable react/jsx-no-target-blank */
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ScaleIcon from '@src/svgs/ScaleIcon';
import lodash from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';

import FadeProducer from '../../fadeEffectProcessor/FadeProducer';
import { numericalInputStyles } from '../../styles/mainStyle';
import UpDownIcon from '../../svgs/UpDownIcon';
import { AspectRatio } from '../../types.ts/video';
import SliderSelect from '../common/PlainSlider';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import ImageFitPropertySelect from './ImageFitPropertySelect';
import { ImagePreset } from './ImagePreset';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';
import { PropertySelect } from './PropertySelect';

import type { ElementState } from '../../renderer/ElementState';

interface ImageSettingsProps {
  activeElement: ElementState;
}
const DEFAULT_FIT_PROPS = [
  { caption: 'Cover', value: 'cover' },
  { caption: 'Contain', value: 'contain' },
  {
    caption: 'Black Frame',
    value: 'black-frame',
  },
  { caption: 'Fill', value: 'fill' },
];

export const ImageSettings: React.FC<ImageSettingsProps> = props => {
  const videoCreator = useVideoCreatorStore();
  const { editorStore } = useStore();
  const fadeProducer = new FadeProducer(videoCreator, props.activeElement);
  const [imageFitProps, setImageFitProps] = useState(DEFAULT_FIT_PROPS);

  // Helper function to update image element properties using headless editor or fallback to legacy
  const updateImageProperty = async (propertyUpdates: Record<string, any>) => {
    if (
      editorStore?.headlessEnabled &&
      editorStore?.editor &&
      props.activeElement?.source?.id
    ) {
      // Use headless editor UpdateElementCommand
      const success = editorStore.editor.commandUpdateElement(
        props.activeElement.source.id,
        propertyUpdates,
      );

      if (!success) {
        console.warn(
          `Failed to update image element properties:`,
          propertyUpdates,
        );
        // Fallback to legacy method
        await videoCreator.applyVideoStateModifications(
          Object.fromEntries(
            Object.entries(propertyUpdates).map(([key, value]) => [
              `${props.activeElement?.source.id}.${key}`,
              value,
            ]),
          ),
          true,
          'changing image settings',
        );
      }
    } else {
      // Fallback to legacy method
      await videoCreator.applyVideoStateModifications(
        Object.fromEntries(
          Object.entries(propertyUpdates).map(([key, value]) => [
            `${props.activeElement?.source.id}.${key}`,
            value,
          ]),
        ),
        true,
        'changing image settings',
      );
    }
  };

  useEffect(() => {
    const aspectRatio = videoCreator.currentVideo?.aspectRatio;
    if (
      aspectRatio === AspectRatio.AR_9_16 ||
      aspectRatio === AspectRatio.AR_1_1
    ) {
      setImageFitProps(imageFitProps.filter(f => f.value !== 'black-frame'));
    } else setImageFitProps(DEFAULT_FIT_PROPS);
  }, [videoCreator.currentVideo?.aspectRatio]);

  const modifyPropertyDebounced = lodash.debounce(
    async (propertyName: string, propertyValue: any) => {
      await updateImageProperty({ [propertyName]: propertyValue });
    },
    250,
  );

  const handleChangeScale = lodash.debounce(async (value: number) => {
    const scale = value + '%';
    await updateImageProperty({
      x_scale: scale,
      y_scale: scale,
    });
  }, 250);

  return (
    <Fragment>
      <ImagePreset
        activeElement={props.activeElement}
        url={props.activeElement.source.source}
      />

      <ElementAnimationSettings activeElement={props.activeElement} />

      <BasicContent>
        <Item>
          <PropertyCaption>Start</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="time"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              const overlayData = await fadeProducer.resetCrossfadeOnVideo(
                parseFloat(value),
                props.activeElement.duration,
              );

              // Apply fade overlays using legacy method (complex video overlay logic)
              if (Object.keys(overlayData).length > 0) {
                await videoCreator.applyVideoStateModifications(
                  overlayData,
                  true,
                  'changing image placement',
                );
              }

              // Update the main property using headless editor
              await updateImageProperty({ [property]: value });

              videoCreator.handleResetPhotoHighlight(
                props.activeElement,
                value,
              );
            }}
          />
        </Item>

        <Item>
          <PropertyCaption>Duration</PropertyCaption>
          <PropertyNumericalInput
            activeElement={props.activeElement}
            propertyName="duration"
            defaultValue="auto"
            unit="s"
            customStyles={numericalInputStyles}
            getInputValue={async (property: string, value: string) => {
              const overlayData = await fadeProducer.resetCrossfadeOnVideo(
                props.activeElement.source.time,
                parseFloat(value),
              );

              const compositionMods =
                videoCreator.imageCompositionDurationModifications(
                  props.activeElement,
                  parseFloat(value),
                );

              // Apply fade overlays and composition modifications using legacy method (complex logic)
              if (
                Object.keys(overlayData).length > 0 ||
                Object.keys(compositionMods).length > 0
              ) {
                await videoCreator.applyVideoStateModifications(
                  {
                    ...overlayData,
                    ...compositionMods,
                  },
                  true,
                  'changing image placement',
                );
              }

              // Update the main property using headless editor
              await updateImageProperty({ [property]: value });

              videoCreator.handleResetPhotoHighlight(
                props.activeElement,
                null,
                value,
              );
            }}
          />
        </Item>

        <Item>
          <PropertyCaption>Fit</PropertyCaption>
          <ImageFitPropertySelect
            activeElement={props.activeElement}
            propertyName="fit"
            defaultValue="cover"
            options={imageFitProps}
          />
        </Item>

        <Item>
          <PropertyCaption>Opacity</PropertyCaption>
          <PropertySelect
            activeElement={props.activeElement}
            propertyName="opacity"
            defaultValue="100%"
            options={[
              { caption: '100%', value: '100%' },
              { caption: '80%', value: '80%' },
              { caption: '60%', value: '60%' },
              { caption: '40%', value: '40%' },
              { caption: '20%', value: '20%' },
            ]}
          />
        </Item>

        {props.activeElement.source.type === 'image' && (
          <>
            <Item>
              <PropertyCaption>Zoom In/Out</PropertyCaption>
              <SliderAndIcon>
                <ScaleIcon />
                <SliderSelect
                  getValue={newValue => handleChangeScale(newValue)}
                  defaultValue={100}
                  currValue={parseFloat(props.activeElement.source.x_scale)}
                  min={100}
                  max={150}
                />
              </SliderAndIcon>
            </Item>
            <Item>
              <PropertyCaption>Vertical Alignment</PropertyCaption>
              <SliderAndIcon>
                <UpDownIcon />
                <SliderSelect
                  getValue={newValue =>
                    modifyPropertyDebounced('y_alignment', newValue + '%')
                  }
                  defaultValue={50}
                  currValue={parseFloat(props.activeElement.source.y_alignment)}
                  min={0}
                  max={100}
                />
              </SliderAndIcon>
            </Item>
          </>
        )}
      </BasicContent>

      <GenericAnimationSettings activeElement={props.activeElement} />
    </Fragment>
  );
};

// const Information = styled.div`
//   margin-top: 20px;
//   color: #a3a5a5;

//   a {
//     color: #a3a5a5;
//   }
// `;

const BasicContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
`;

const Item = styled.div``;

const SliderAndIcon = styled.div`
  display: flex;
  border: 1px solid #484848;
  border-radius: 8px;
  padding: 12px;
  gap: 5px;
  align-items: center;
  height: 15px;
`;
